import { iconSvg, type IconName } from '../panels.js';

/**
 * The icons the phone grid needs beyond `panels.ts`.
 *
 * Same drawing rules as the shared set: stroked paths in a 24-box, no fill, no
 * icon font — a shop's theme cannot fail to load them and they inherit
 * `currentColor`. Anything already in `panels.ts` is reused by name rather
 * than redrawn here, so both shells show the same glyph for the same idea.
 */
const PHONE_PATHS = {
  /** A pencil: "Uredi". */
  pencil: 'M4 20l1-4.5L15.6 4.9a2 2 0 0 1 2.8 0l.7.7a2 2 0 0 1 0 2.8L8.5 19 4 20zM14.4 6.1l3.5 3.5',
  /** Two letters: "Font". */
  font: 'M3 18l4.5-12L12 18M4.6 14h5.8M15 18V9.5h3.2a2.6 2.6 0 0 1 0 5.2H15',
  /** A droplet: colour and recolour. */
  droplet: 'M12 3.5s5.5 6 5.5 9.6a5.5 5.5 0 0 1-11 0C6.5 9.5 12 3.5 12 3.5z',
  /** A paint bucket: shape fill. */
  bucket: 'M7 4.5l9 9-6.2 6.2a2 2 0 0 1-2.8 0l-4.2-4.2a2 2 0 0 1 0-2.8zM4.5 12h11M19 16.5c0 1-.8 1.8-1.8 1.8s-1.8-.8-1.8-1.8 1.8-3 1.8-3 1.8 2 1.8 3z',
  /** Three weights: line thickness. */
  weight: 'M4 6.5h16M4 12h16M4 18.5h16',
  /** Rows with an up/down arrow: line height. */
  lineHeight: 'M9 6h11M9 12h11M9 18h11M4.5 5.5v13M2.5 7.5l2-2 2 2M2.5 16.5l2 2 2-2',
  /** A T turned on its side: text orientation. */
  orientation: 'M5 5h9M9.5 5v14M17.5 8v9M15 8h5M4 21h6',
  /** Four tiles: pattern / tiling. */
  pattern: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  /** Arrows pointing inwards: fit inside the zone. */
  fitZone: 'M4 4h16v16H4zM9.5 9.5l-3.5-3.5M9.5 9.5V6.6M9.5 9.5H6.6M14.5 14.5l3.5 3.5M14.5 14.5v2.9M14.5 14.5h2.9',
  /** Arrows pointing outwards: fill the zone. */
  fillZone: 'M4 4h16v16H4zM6.5 6.5l3.5 3.5M6.5 6.5v3M6.5 6.5h3M17.5 17.5L14 14M17.5 17.5v-3M17.5 17.5h-3',
  /** Overlapping circles: filters. */
  filters: 'M9.5 5.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11zM14.5 5.5a5.5 5.5 0 1 1 0 11',
  /** Two arrows in a loop: replace the picture. */
  swap: 'M4 8.5h12.5l-3-3M20 15.5H7.5l3 3',
  /** A framed picture: background. */
  backdrop: 'M3.5 5h17v14h-17zM3.5 14.5l4.2-4.2 3 3 3.6-3.6 6.2 6.2',
  /** Clipboard: paste. */
  paste: 'M8 5h8v3H8zM6.5 6.5H5v14h14v-14h-1.5M9 13h6M9 16.5h4',
  /** Three dots: the More menu. */
  dots: 'M6 12h.01M12 12h.01M18 12h.01',
  /** A short arrow into a tray: send backward / forward is the same glyph mirrored. */
  toBack: 'M4 4h10v10H4zM10 10h10v10H10z',
  /** Two posts with an arrow between them: the wrap column's width. */
  textWidth: 'M4 5v14M20 5v14M7.5 12h9M7.5 12l2.5-2.5M7.5 12l2.5 2.5M16.5 12L14 9.5M16.5 12L14 14.5',
} as const;

export type PhoneIconName = IconName | keyof typeof PHONE_PATHS;

/** Wider strokes for the dot menu, which would otherwise disappear. */
const THICK: ReadonlySet<string> = new Set(['dots']);

/** The icon as SVG markup; falls back to the shared set for shared names. */
export function phoneIconSvg(name: PhoneIconName, size = 20): string {
  const path = (PHONE_PATHS as Record<string, string | undefined>)[name];
  if (!path) return iconSvg(name as IconName, size);
  const width = THICK.has(name) ? 2.6 : 1.7;
  return (
    `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor"`
    + ` stroke-width="${width}" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">`
    + `<path d="${path}"/></svg>`
  );
}
